import React from 'react';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { SmartHeader, SmartField, NoRecordsCard } from './_SmartFieldRenderer';

const Section = ({ icon, title, children }) => (
    <div className="pt-3 pb-3 border-bottom-1 surface-border">
        <div className="flex align-items-center gap-2 mb-3">
            <i className={`pi ${icon} text-primary`} aria-hidden="true" />
            <span className="text-sm font-semibold text-900 uppercase">{title}</span>
        </div>
        <div className="grid">{children}</div>
    </div>
);

const formatDate = (s) => {
    if (!s) return null;
    try {
        const d = new Date(s);
        if (isNaN(d.getTime())) return s;
        return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: '2-digit' });
    } catch {
        return s;
    }
};

const RCDetailsOutputComponent = ({ reportData, serviceDetails, isSharedView, hideCredits, onDownloadPDF, pdfPasswordHint }) => {
    const shouldHideCredits = hideCredits || isSharedView;

    if (reportData?.code === '1004') {
        return <NoRecordsCard subtitle="There is no data found for the provided details. Please enter valid details." />;
    }

    const isStoredReport = reportData?.report_json || reportData?.api_response || reportData?.data;
    let parsedDataForCheck = reportData;
    if (reportData?.report_json && typeof reportData.report_json === 'object') {
        parsedDataForCheck = reportData.report_json;
    }
    const errorCode =
        reportData?.code ||
        reportData?.data?.data?.code ||
        parsedDataForCheck?.data?.data?.code ||
        reportData?.report_json?.data?.data?.code;
    const hasValidData = errorCode === '1000' || errorCode === 1000;

    if (!isStoredReport && !hasValidData && errorCode === '1001') {
        return (
            <NoRecordsCard subtitle="No vehicle registration records found for the provided RC number in the RTO database. Please verify and try again." />
        );
    }

    if (!isStoredReport && !hasValidData && errorCode === '1002') {
        return (
            <Card className="text-center">
                <i className="pi pi-exclamation-triangle text-5xl text-orange-500 block mb-3" aria-hidden="true" />
                <h3 className="text-lg font-semibold text-900 m-0 mb-2">Multiple Vehicle Records Found</h3>
                <p className="text-600 text-sm m-0">
                    Multiple vehicle registration records found for this RC number across different RTOs. Please contact our support team for manual verification.
                </p>
            </Card>
        );
    }

    const hasValidRCData =
        reportData?.raw_response?.data?.data?.rc_data ||
        reportData?.data?.raw_response?.data?.data?.rc_data ||
        reportData?.data?.data?.rc_data ||
        reportData?.data?.rc_details?.raw_response?.data?.data?.rc_data ||
        reportData?.rc_details?.raw_response?.data?.data?.rc_data ||
        reportData?.data?.rc_details ||
        reportData?.rc_details ||
        isStoredReport;

    if (!hasValidRCData && !isStoredReport && (reportData?.code === 'BAD_REQUEST' || (!reportData?.success && reportData?.code !== 'SERVER_ERROR'))) {
        return (
            <Card className="text-center">
                <i className="pi pi-times-circle text-5xl text-red-500 block mb-3" aria-hidden="true" />
                <h3 className="text-lg font-semibold text-900 m-0 mb-2">RC Verification Failed</h3>
                <p className="text-600 text-sm m-0">{reportData?.message || 'Invalid RC number. Please enter a valid RC number.'}</p>
            </Card>
        );
    }

    if (
        !isStoredReport &&
        (reportData?.code === 'SERVER_ERROR' ||
            (reportData?.data?.raw_response?.status_code === 500 && !reportData?.code) ||
            (reportData?.data?.raw_response?.success === false && reportData?.data?.raw_response?.error && !reportData?.code))
    ) {
        return (
            <Card className="text-center">
                <i className="pi pi-exclamation-triangle text-5xl text-orange-500 block mb-3" aria-hidden="true" />
                <h3 className="text-lg font-semibold text-900 m-0 mb-2">Server Error</h3>
                <p className="text-600 text-sm m-0">There was a server error from the API provider. Please try again later. No credits were deducted.</p>
            </Card>
        );
    }

    let parsedData = reportData;
    if (typeof reportData === 'string') {
        try { parsedData = JSON.parse(reportData); } catch { /* ignore */ }
    }
    if (reportData?.report_json && typeof reportData.report_json === 'object') parsedData = reportData.report_json;
    if (parsedData?.report_json && parsedData.report_json !== parsedData) {
        if (typeof parsedData.report_json === 'string') {
            try { parsedData = JSON.parse(parsedData.report_json); } catch { /* ignore */ }
        } else if (typeof parsedData.report_json === 'object') {
            parsedData = parsedData.report_json;
        }
    }

    let rcRawData = null;
    let rcDetails = null;
    if (parsedData?.data?.data?.rc_data) rcRawData = parsedData.data.data.rc_data;
    else if (parsedData?.raw_response?.data?.data?.rc_data) rcRawData = parsedData.raw_response.data.data.rc_data;
    else if (parsedData?.data?.raw_response?.data?.data?.rc_data) rcRawData = parsedData.data.raw_response.data.data.rc_data;
    else if (parsedData?.data?.rc_details?.raw_response?.data?.data?.rc_data) rcRawData = parsedData.data.rc_details.raw_response.data.data.rc_data;
    else if (parsedData?.rc_details?.raw_response?.data?.data?.rc_data) rcRawData = parsedData.rc_details.raw_response.data.data.rc_data;
    else if (parsedData?.data?.rc_details) rcDetails = parsedData.data.rc_details;
    else if (parsedData?.rc_details) rcDetails = parsedData.rc_details;
    else if (parsedData?.rc_data) rcRawData = parsedData.rc_data;
    else if (parsedData?.data?.rc_data) rcRawData = parsedData.data.rc_data;
    else if (parsedData?.document_id || parsedData?.owner_data || parsedData?.vehicle_data) rcRawData = parsedData;
    else rcDetails = parsedData;

    if (rcRawData) {
        rcDetails = {
            rc_number: rcRawData.document_id || rcRawData.documentId || rcRawData.rc_number || null,
            registration_date: rcRawData.issue_date || rcRawData.issueDate || rcRawData.registration_date || null,
            expiry_date: rcRawData.expiry_date || null,
            registered_at: rcRawData.registered_at || null,
            status: rcRawData.status || null,
            blacklist_status: rcRawData.blacklist_status || null,
            norms_type: rcRawData.norms_type || null,
            is_commercial: rcRawData.is_commercial || null,
            owner_name: rcRawData.owner_data?.name || rcRawData.ownerData?.name || rcRawData.owner_name || null,
            father_name: rcRawData.owner_data?.father_name || rcRawData.ownerData?.fatherName || rcRawData.father_name || null,
            present_address: rcRawData.owner_data?.present_address || rcRawData.ownerData?.presentAddress || rcRawData.present_address || null,
            permanent_address: rcRawData.owner_data?.permanent_address || rcRawData.ownerData?.permanentAddress || rcRawData.permanent_address || null,
            mobile_number: rcRawData.owner_data?.mobile_number || rcRawData.ownerData?.mobileNumber || rcRawData.mobile_number || null,
            vehicle_class: rcRawData.vehicle_data?.category_description || rcRawData.vehicleData?.categoryDescription || rcRawData.vehicle_class || null,
            fuel_type: rcRawData.vehicle_data?.fuel_type || rcRawData.vehicleData?.fuelType || rcRawData.fuel_type || null,
            maker_model: rcRawData.vehicle_data?.maker_model || rcRawData.vehicleData?.makerModel || rcRawData.maker_model || null,
            maker_description: rcRawData.vehicle_data?.maker_description || rcRawData.vehicleData?.makerDescription || null,
            chassis_number: rcRawData.vehicle_data?.chassis_number || rcRawData.vehicleData?.chassisNumber || rcRawData.chassis_number || null,
            engine_number: rcRawData.vehicle_data?.engine_number || rcRawData.vehicleData?.engineNumber || rcRawData.engine_number || null,
            body_type: rcRawData.vehicle_data?.body_type || rcRawData.vehicleData?.bodyType || rcRawData.body_type || null,
            color: rcRawData.vehicle_data?.color || rcRawData.vehicleData?.color || rcRawData.color || null,
            cubic_capacity: rcRawData.vehicle_data?.cubic_capacity || rcRawData.vehicleData?.cubicCapacity || rcRawData.cubic_capacity || null,
            manufactured_date: rcRawData.vehicle_data?.manufactured_date || rcRawData.vehicleData?.manufacturedDate || rcRawData.manufactured_date || null,
            seating_capacity: rcRawData.vehicle_data?.seating_capacity || rcRawData.vehicleData?.seatingCapacity || rcRawData.seating_capacity || null,
            gross_weight: rcRawData.vehicle_data?.gross_weight || rcRawData.vehicleData?.grossWeight || null,
            unladen_weight: rcRawData.vehicle_data?.unladen_weight || rcRawData.vehicleData?.unladenWeight || null,
            number_of_cylinders: rcRawData.vehicle_data?.number_of_cylinders || rcRawData.vehicleData?.numberOfCylinders || null,
            wheelbase: rcRawData.vehicle_data?.wheelbase || rcRawData.vehicleData?.wheelbase || null,
            fitness_validity: rcRawData.validity?.fitness || rcRawData.fitness_validity || null,
            insurance_validity: rcRawData.validity?.insurance || rcRawData.insurance_validity || rcRawData.insurance_data?.expiry_date || null,
            pucc_validity: rcRawData.validity?.pucc || rcRawData.pucc_validity || null,
            tax_validity: rcRawData.validity?.tax || rcRawData.tax_validity || rcRawData.tax_end_date || null,
            permit_validity: rcRawData.validity?.permit || rcRawData.permit_validity || null,
            insurance_policy_number: rcRawData.insurance_data?.policy_number || null,
            insurance_company: rcRawData.insurance_data?.company || null,
            financer_name: rcRawData.financer?.name || rcRawData.financer_name || null,
        };
    }

    if (!rcDetails) {
        return <NoRecordsCard subtitle="The RC details data structure is not recognized. Please try again." />;
    }

    const remainingBalance = serviceDetails?.available_credits;

    const blacklistTagProps =
        rcDetails.blacklist_status === 'false'
            ? { value: 'Clear', severity: 'success' }
            : rcDetails.blacklist_status === 'true'
            ? { value: 'Blacklisted', severity: 'danger' }
            : { value: 'Not Provided', severity: 'secondary' };

    return (
        <div>
            <SmartHeader title="RC Details Verification Report" icon="pi-car" remainingBalance={remainingBalance} shouldHideCredits={shouldHideCredits} />

            <Card>
                {onDownloadPDF && (
                    <div className="flex justify-content-end mb-2">
                        <Button
                            label="Download PDF"
                            icon="pi pi-file-pdf"
                            size="small"
                            outlined
                            onClick={onDownloadPDF}
                        />
                    </div>
                )}

                {onDownloadPDF && pdfPasswordHint && (
                    <div className="surface-100 border-round border-left-3 border-primary p-2 mb-2 flex align-items-start gap-2">
                        <i className="pi pi-lock text-primary text-sm mt-1" aria-hidden="true" />
                        <span className="text-600 text-sm line-height-3">{pdfPasswordHint}</span>
                    </div>
                )}

                <Section icon="pi-car" title="Vehicle Information">
                    <SmartField name="rc_number" value={rcDetails.rc_number} highlight />
                    <SmartField name="registration_date" value={formatDate(rcDetails.registration_date)} />
                    <SmartField name="vehicle_class" value={rcDetails.vehicle_class} />
                    <SmartField name="fuel_type" value={rcDetails.fuel_type} />
                    <SmartField name="make_and_model" value={rcDetails.maker_model} />
                    <SmartField name="manufacturer" value={rcDetails.maker_description} />
                    <SmartField name="color" value={rcDetails.color} />
                    <SmartField name="body_type" value={rcDetails.body_type} />
                    <SmartField name="seating_capacity" value={rcDetails.seating_capacity} />
                    {rcDetails.is_commercial !== null && (
                        <div className="col-12 sm:col-6 lg:col-4 flex flex-column gap-1">
                            <span className="text-500 text-xs font-medium">Commercial Vehicle</span>
                            <span><Tag value={rcDetails.is_commercial ? 'Yes' : 'No'} severity={rcDetails.is_commercial ? 'warning' : 'success'} /></span>
                        </div>
                    )}
                </Section>

                <Section icon="pi-cog" title="Technical Details">
                    <SmartField name="chassis_number" value={rcDetails.chassis_number} />
                    <SmartField name="engine_number" value={rcDetails.engine_number} />
                    <SmartField name="cubic_capacity" value={rcDetails.cubic_capacity ? `${rcDetails.cubic_capacity} CC` : null} />
                    <SmartField name="manufacturing_date" value={formatDate(rcDetails.manufactured_date)} />
                    <SmartField name="gross_weight" value={rcDetails.gross_weight ? `${rcDetails.gross_weight} KG` : null} />
                    <SmartField name="unladen_weight" value={rcDetails.unladen_weight ? `${rcDetails.unladen_weight} KG` : null} />
                    <SmartField name="number_of_cylinders" value={rcDetails.number_of_cylinders} />
                    <SmartField name="wheelbase" value={rcDetails.wheelbase ? `${rcDetails.wheelbase} MM` : null} />
                </Section>

                <Section icon="pi-user" title="Owner Information">
                    <SmartField name="owner_name" value={rcDetails.owner_name} />
                    <SmartField name="father_name" value={rcDetails.father_name} />
                    <SmartField name="mobile_number" value={rcDetails.mobile_number} />
                    <SmartField name="financer" value={rcDetails.financer_name} />
                </Section>

                {(rcDetails.present_address || rcDetails.permanent_address) && (
                    <Section icon="pi-map-marker" title="Address Information">
                        {rcDetails.present_address && (
                            <div className="col-12 flex flex-column gap-1">
                                <span className="text-500 text-xs font-medium">Present Address</span>
                                <span className="text-900 text-sm font-semibold" style={{ wordBreak: 'break-word' }}>{rcDetails.present_address}</span>
                            </div>
                        )}
                        {rcDetails.permanent_address && (
                            <div className="col-12 flex flex-column gap-1">
                                <span className="text-500 text-xs font-medium">Permanent Address</span>
                                <span className="text-900 text-sm font-semibold" style={{ wordBreak: 'break-word' }}>{rcDetails.permanent_address}</span>
                            </div>
                        )}
                    </Section>
                )}

                <Section icon="pi-shield" title="Insurance Information">
                    <SmartField name="policy_number" value={rcDetails.insurance_policy_number} />
                    <SmartField name="insurance_company" value={rcDetails.insurance_company} />
                    <SmartField name="insurance_valid_till" value={formatDate(rcDetails.insurance_validity)} />
                    <div className="col-12 sm:col-6 lg:col-4 flex flex-column gap-1">
                        <span className="text-500 text-xs font-medium">Blacklist Status</span>
                        <span><Tag {...blacklistTagProps} /></span>
                    </div>
                    <SmartField name="norms_type" value={rcDetails.norms_type} />
                    <SmartField name="registered_at" value={rcDetails.registered_at} />
                </Section>

                <Section icon="pi-calendar" title="Validity Information">
                    <SmartField name="fitness_valid_till" value={formatDate(rcDetails.fitness_validity)} />
                    <SmartField name="insurance_valid_till" value={formatDate(rcDetails.insurance_validity)} />
                    <SmartField name="pucc_valid_till" value={formatDate(rcDetails.pucc_validity)} />
                    <SmartField name="tax_valid_till" value={formatDate(rcDetails.tax_validity)} />
                    <SmartField name="permit_valid_till" value={formatDate(rcDetails.permit_validity)} />
                    <div className="col-12 sm:col-6 lg:col-4 flex flex-column gap-1">
                        <span className="text-500 text-xs font-medium">Status</span>
                        <span>
                            <Tag
                                value={rcDetails.status || 'Not Provided'}
                                severity={rcDetails.status === 'ACTIVE' ? 'success' : 'warning'}
                            />
                        </span>
                    </div>
                </Section>
            </Card>
        </div>
    );
};

export default RCDetailsOutputComponent;
